import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email.trim().toLowerCase(), password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate("/");
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left panel - form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-10">
            <div className="w-9 h-9 rounded-md bg-[#0055FF] flex items-center justify-center text-white font-bold">D</div>
            <div className="text-sm font-semibold tracking-tight">Digital <span className="text-[#0055FF]">Yantra</span></div>
          </div>

          <div className="mb-8">
            <div className="text-[11px] uppercase tracking-wider text-[#64748B] font-mono mb-2">Sign in</div>
            <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight text-[#0A0A0A]">
              Operate your store, <span className="text-[#0055FF]">faster.</span>
            </h1>
            <p className="text-sm text-[#475569] mt-2">
              Inventory testing, sales tracking, and service receipts — all in one place.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-medium text-[#475569] uppercase tracking-wide">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                data-testid="login-email"
                className="h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-medium text-[#475569] uppercase tracking-wide">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="login-password"
                className="h-11"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-11 bg-[#0055FF] hover:bg-[#0040C1] text-white font-medium"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
        </div>
      </div>

      {/* Right panel - visual */}
      <div className="hidden lg:block relative overflow-hidden bg-[#0A0A0A]">
        <img
          src="https://images.unsplash.com/photo-1768167630789-925bc3c70db7?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A]/80 via-transparent to-[#0055FF]/30" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <div className="text-[11px] uppercase tracking-[0.2em] font-mono opacity-70 mb-3">Built for daily operations</div>
          <h2 className="text-3xl font-display font-medium leading-tight max-w-md">
            Test, stock, sell and service refurbished laptops without the spreadsheet chaos.
          </h2>
          <div className="mt-8 grid grid-cols-3 gap-6 max-w-md">
            <div>
              <div className="text-2xl font-display font-semibold">3</div>
              <div className="text-xs text-white/70 font-mono uppercase tracking-wider">Modules</div>
            </div>
            <div>
              <div className="text-2xl font-display font-semibold">1-click</div>
              <div className="text-xs text-white/70 font-mono uppercase tracking-wider">Receipts</div>
            </div>
            <div>
              <div className="text-2xl font-display font-semibold">A5</div>
              <div className="text-xs text-white/70 font-mono uppercase tracking-wider">Print ready</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
